import React, { useState } from 'react';
import { Button } from './ui/button';
import { Loader2, Link2, CheckCircle, AlertCircle, X, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  const t = localStorage.getItem('admin_token');
  return t ? { 'Authorization': `Bearer ${t}`, 'Content-Type': 'application/json' } : {};
};

const BulkConvertSpeakers = () => {
  const [scanning, setScanning] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState({});
  const [applying, setApplying] = useState(false);
  const [showUnmatched, setShowUnmatched] = useState(false);

  const scan = async () => {
    setScanning(true);
    try {
      const res = await fetch(`${API_URL}/api/speakers/bulk-convert/preview`, {
        method: 'POST', headers: authHeaders(),
      });
      if (!res.ok) throw new Error('Scan failed');
      const data = await res.json();
      setPreview(data);
      // Auto-select all high-confidence matches
      const sel = {};
      data.matches.forEach((m, i) => { sel[i] = true; });
      setSelected(sel);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setScanning(false);
    }
  };

  const apply = async () => {
    const matches = preview.matches.filter((_, i) => selected[i]);
    const cleanup = preview.stale_text || [];
    if (!matches.length && !cleanup.length) { toast.error('Nothing to apply'); return; }
    setApplying(true);
    try {
      const res = await fetch(`${API_URL}/api/speakers/bulk-convert/apply`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ matches, cleanup }),
      });
      if (!res.ok) throw new Error('Apply failed');
      const data = await res.json();
      toast.success(data.message);
      setPreview(null);
      setSelected({});
    } catch (e) {
      toast.error(e.message);
    } finally {
      setApplying(false);
    }
  };

  if (!preview) {
    return (
      <div className="flex items-center gap-3" data-testid="bulk-convert-trigger">
        <Button variant="outline" size="sm" onClick={scan} disabled={scanning} data-testid="bulk-convert-scan-btn">
          {scanning ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Link2 className="w-4 h-4 mr-1.5" />}
          {scanning ? 'Scanning...' : 'Link Session Speakers'}
        </Button>
        <span className="text-xs text-slate-500">Match plain-text speaker names to speaker entities</span>
      </div>
    );
  }

  const totalMatches = preview.matches.length;
  const totalUnmatched = preview.unmatched.length;
  const totalStale = (preview.stale_text || []).length;
  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <div className="border rounded-xl bg-white shadow-sm" data-testid="bulk-convert-preview">
      {/* Header */}
      <div className="px-5 py-4 border-b flex items-center justify-between">
        <div>
          <h3 className="font-bold text-base text-slate-900">Speaker Linking Preview</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Scanned {preview.total_sessions_scanned} sessions against {preview.total_speakers_in_db} speaker entities
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={() => { setPreview(null); setSelected({}); }}>
            <X className="w-4 h-4 mr-1" /> Cancel
          </Button>
          <Button
            size="sm"
            onClick={apply}
            disabled={applying || (selectedCount === 0 && (preview.stale_text || []).length === 0)}
            data-testid="bulk-convert-apply-btn"
          >
            {applying ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle className="w-4 h-4 mr-1" />}
            Link {selectedCount} Speaker{selectedCount !== 1 ? 's' : ''}{totalStale > 0 ? ` + Clean ${totalStale}` : ''}
          </Button>
        </div>
      </div>

      {/* Matches */}
      {totalMatches > 0 && (
        <div className="px-5 py-3">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span className="text-sm font-semibold text-green-800">{totalMatches} Match{totalMatches !== 1 ? 'es' : ''} Found</span>
            <button
              onClick={() => {
                const allSelected = Object.values(selected).every(Boolean);
                const next = {};
                preview.matches.forEach((_, i) => { next[i] = !allSelected; });
                setSelected(next);
              }}
              className="ml-auto text-xs text-blue-600 hover:underline"
            >
              {Object.values(selected).every(Boolean) ? 'Deselect All' : 'Select All'}
            </button>
          </div>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {preview.matches.map((m, i) => (
              <label
                key={i}
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${selected[i] ? 'bg-green-50 border-green-200' : 'bg-white border-slate-200 hover:bg-slate-50'}`}
                data-testid={`match-row-${i}`}
              >
                <input
                  type="checkbox"
                  checked={!!selected[i]}
                  onChange={() => setSelected(prev => ({ ...prev, [i]: !prev[i] }))}
                  className="mt-1 accent-green-600"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-slate-500 line-through">{m.plain_text}</span>
                    <span className="text-slate-400">&rarr;</span>
                    <span className="text-sm font-semibold text-slate-900">{m.matched_speaker_name}</span>
                    {m.matched_speaker_title && (
                      <span className="text-xs text-slate-500">{m.matched_speaker_title}</span>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5">Session: {m.session_title}</p>
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${m.confidence === 'high' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                  {m.confidence}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Stale text (already linked but text not cleaned) */}
      {totalStale > 0 && (
        <div className="px-5 py-3 border-t">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="w-4 h-4 text-blue-500" />
            <span className="text-sm font-semibold text-blue-800">{totalStale} Stale Text Entr{totalStale !== 1 ? 'ies' : 'y'} to Clean</span>
          </div>
          <p className="text-xs text-slate-500 mb-2">These speakers are already linked but the old plain-text remains. Applying will clean them up.</p>
          <div className="space-y-1.5">
            {preview.stale_text.map((s, i) => (
              <div key={i} className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg text-sm">
                <span className="text-slate-500 line-through">{s.plain_text}</span>
                <span className="text-slate-400">&rarr;</span>
                <span className="text-blue-700 font-medium">{s.linked_speaker_name}</span>
                <span className="text-xs text-slate-400 ml-auto whitespace-nowrap">in: {s.session_title}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Unmatched */}
      {totalUnmatched > 0 && (
        <div className="px-5 py-3 border-t">
          <button
            onClick={() => setShowUnmatched(!showUnmatched)}
            className="flex items-center gap-2 text-sm font-semibold text-amber-700 hover:text-amber-800"
          >
            <AlertCircle className="w-4 h-4 text-amber-500" />
            {totalUnmatched} Unmatched — needs manual linking
            {showUnmatched ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          {showUnmatched && (
            <div className="mt-2 space-y-1.5">
              {preview.unmatched.map((u, i) => (
                <div key={i} className="flex items-center gap-2 p-2 bg-amber-50 rounded-lg text-sm" data-testid={`unmatched-row-${i}`}>
                  <AlertCircle className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                  <span className="text-slate-700">{u.plain_text}</span>
                  <span className="text-xs text-slate-400 ml-auto whitespace-nowrap">in: {u.session_title}</span>
                </div>
              ))}
              <p className="text-xs text-slate-500 mt-2">
                Tip: Add these speakers to the Speakers page first, then re-scan to link them.
              </p>
            </div>
          )}
        </div>
      )}

      {totalMatches === 0 && totalUnmatched === 0 && totalStale === 0 && (
        <div className="px-5 py-8 text-center text-sm text-slate-500">
          No plain-text speakers found in any session. All sessions are either empty or already linked.
        </div>
      )}
    </div>
  );
};

export default BulkConvertSpeakers;
